import { CatenaryUtils } from './CatenaryUtils.js';
import { GLAZE_ICE_DENSITY, RIME_ICE_DENSITY } from './Constants.js';

/**
 * Calculates horizontal tension of cable given weather change
 * @param {LookupCable || LookupBundle} lookupCable Cable
 * @param {LoadCase} finalLoadCase Load case we want to see the cable in
 * @returns {object} Inputs and cable at final load case
 */
export function transformLookupCable(lookupCable, finalLoadCase) {
  const geometricCable = _lookup2Geometric(lookupCable);
  const {
    spanLength,
    diameter,
    linearWeight,
    crossSectionalArea,
    youngsMod,
    thermalExpansionCoeff,
    catenaryParam,
    catenaryXc,
    catenaryYc
  } = geometricCable;

  const initialLoadCase = lookupCable.loadCase;
  const ΔTemp = finalLoadCase.temperature - initialLoadCase.temperature;

  // Calc initial state
  const initialStressedLength = CatenaryUtils.calculateArcLength(catenaryParam, catenaryXc, spanLength);
  const initialHorizontalTension = lookupCable.horizontalTension;

  // Calc elongation effects
  const initialUnstressedLength = initialStressedLength / (1 + initialHorizontalTension / (crossSectionalArea * youngsMod));
  const finalUnstressedLength = initialUnstressedLength * (1 + thermalExpansionCoeff * ΔTemp);

  // Calc final geometry
  const finalTotalLinearWeight = calcTotalLinearWeight(geometricCable, finalLoadCase);
  const tensionObjectiveFunc = _generateElasticObjectiveFunc(
    finalTotalLinearWeight,
    0.0,
    0.0,
    spanLength,
    crossSectionalArea,
    youngsMod,
    finalUnstressedLength
  );
  const rootResults = _modifiedNewton(tensionObjectiveFunc, initialHorizontalTension);
  const finalHorizontalTension = rootResults.root;

  // Construct lookup cable in final weather condition
  const finalLookupCable = new LookupCable(
    spanLength,
    diameter,
    linearWeight,
    crossSectionalArea,
    youngsMod,
    thermalExpansionCoeff,
    finalLoadCase,
    finalHorizontalTension
  );

  // Organize results
  const results = {
    inputs: {
      cable: lookupCable,
      finalLoadCase: finalLoadCase
    },
    converged: rootResults.converged,
    rootResults,
    finalState: finalLookupCable
  };

  return results;
}

export function transformGeometricCable(geometricCable, finalLoadCase, rootAbsoluteHeight, leafAbsoluteHeight) {
  const initialLoadCase = geometricCable.loadCase;
  const ΔTemp = finalLoadCase.temperature - initialLoadCase.temperature;

  // Calc initial state
  const initialStressedLength = CatenaryUtils.calculateArcLength(
    geometricCable.catenaryParam,
    geometricCable.catenaryXc,
    geometricCable.spanLength
  );
  const initialHorizontalTension = geometricCable.linearWeight * geometricCable.catenaryParam;

  // Calc elongation effects
  const initialUnstressedLength =
    initialStressedLength / (1 + initialHorizontalTension / (geometricCable.crossSectionalArea * geometricCable.youngsMod));
  const finalUnstressedLength = initialUnstressedLength * (1 + geometricCable.thermalExpansionCoeff * ΔTemp);

  // Calc final geometry
  const finalTotalLinearWeight = calcTotalLinearWeight(geometricCable, finalLoadCase);
  const tensionObjectiveFunc = _generateElasticObjectiveFunc(
    finalTotalLinearWeight,
    rootAbsoluteHeight,
    leafAbsoluteHeight,
    geometricCable.spanLength,
    geometricCable.crossSectionalArea,
    geometricCable.youngsMod,
    finalUnstressedLength
  );
  const rootResults = _modifiedNewton(tensionObjectiveFunc, initialHorizontalTension);
  const finalHorizontalTension = rootResults.root;

  const { catenaryParam, centerXCoordinate, centerYCoordinate, arcLength } = CatenaryUtils.solveParams(
    finalHorizontalTension,
    geometricCable.linearWeight,
    rootAbsoluteHeight,
    leafAbsoluteHeight,
    geometricCable.spanLength
  );

  const finalCable = new GeometricCable(
    geometricCable.spanLength,
    geometricCable.diameter,
    geometricCable.linearWeight,
    geometricCable.crossSectionalArea,
    geometricCable.youngsMod,
    geometricCable.thermalExpansionCoeff,
    finalLoadCase,
    catenaryParam,
    centerXCoordinate,
    centerYCoordinate
  );

  // Organize results
  const results = {
    inputs: {
      cable: geometricCable,
      finalLoadCase: finalLoadCase
    },
    converged: rootResults.converged,
    finalState: finalCable
  };

  return results;
}

/**
 * Calculates horizontal tension of cable given weather change
 * @param {LookupBundle} bundle
 * @param {LoadCase} finalLoadCase
 */
export function transformLookupBundle(bundle, finalLoadCase) {
  const messengerCable = new LookupCable(
    bundle.spanLength,
    bundle.diameter,
    bundle.messengerLinearWeight,
    bundle.crossSectionalArea,
    bundle.youngsMod,
    bundle.thermalExpansionCoeff,
    bundle.loadCase,
    bundle.horizontalTension
  );
  //
  const installCondition = new LoadCase(0, 0, 60, 0, 'Install Condition [60°F]');
  const installedLookupMessenger = transformLookupCable(messengerCable, installCondition);
  const installedBundleCondition = new LoadCase(0, 0, 60, bundle.bundleComponentLinearWeight, 'Accounts for bundle component weight');
  const installedBundle = transformLookupCable(installedLookupMessenger.finalState, installedBundleCondition);
  installedBundle.finalState.linearWeight += bundle.bundleComponentLinearWeight;
  installedBundle.finalState.loadCase = installCondition;

  // Calc cable after final weather condition
  const finalBundle = transformLookupCable(installedBundle.finalState, finalLoadCase);
  // Postprocess results
  const substepConvergence = {
    installedLookupMessenger: installedLookupMessenger.converged,
    installedBundle: installedBundle.converged,
    finalBundle: finalBundle.converged
  };
  const converged = Object.values(substepConvergence).every((converged) => converged);

  const results = {
    inputs: {
      bundle: bundle,
      finalLoadCase: finalLoadCase
    },
    substepConvergence,
    converged,
    finalState: finalBundle.finalState
  };

  return results;
}

/**
 * Generates error function used to solve final tension after thermal expansion.
 * Essentially difference between stretched truss length of initial length 'unstressedLength' and arc length of catenary that has tension 'tension'
 * @param {number} linearWeight Linear weight of cable [lb/ft]
 * @param {number} rootAbsoluteHeight Absolute attach height of cable at root pole [ft]
 * @param {number} leafAbsoluteHeight Absolute attach height of cable at leaf pole [ft]
 * @param {number} spanLengh Span length [ft]
 * @param {number} crossSectionalArea Cross-sectional area of cable [in^2]
 * @param {number} youngsMod Equivalent Young's Modulus of cable [psi]
 * @param {number} unstressedLength Unstressed length of cable [ft]
 * @param {number} iceLinearWeight Linear weight of ice sheath [lb/ft]
 * @returns {function} Objective function
 */
function _generateElasticObjectiveFunc(
  totalLinearWeight,
  rootAbsoluteHeight,
  leafAbsoluteHeight,
  spanLength,
  crossSectionalArea,
  youngsMod,
  unstressedLength
) {
  let frozenErrorFunc = (tension) => {
    let catenaryParams = CatenaryUtils.solveParams(tension, totalLinearWeight, rootAbsoluteHeight, leafAbsoluteHeight, spanLength);
    let elasticLength = unstressedLength * (1 + tension / (crossSectionalArea * youngsMod));
    let error = catenaryParams.arcLength - elasticLength;

    return error;
  };

  return frozenErrorFunc;
}

/**
 * Converts LookupCable to GeometricCable (assumes even attach heights)
 * Catenary information accounts for ice and wind
 * @param {LookupCable} lookupCable Cable to convert
 * @returns {GeometricCable} Converted cable
 */
function _lookup2Geometric(lookupCable) {
  const totalLinearWeight = calcTotalLinearWeight(lookupCable, lookupCable.loadCase); //Including ice and wind
  const { catenaryParam, centerXCoordinate, centerYCoordinate, arcLength } = CatenaryUtils.solveParams(
    lookupCable.horizontalTension,
    totalLinearWeight,
    0.0,
    0.0,
    lookupCable.spanLength
  );

  const convertedCable = new GeometricCable(
    lookupCable.spanLength,
    lookupCable.diameter,
    lookupCable.linearWeight,
    lookupCable.crossSectionalArea,
    lookupCable.youngsMod,
    lookupCable.thermalExpansionCoeff,
    lookupCable.loadCase,
    catenaryParam,
    centerXCoordinate,
    centerYCoordinate
  );

  return convertedCable;
}

/**
 * Calculates the linear weight of cable accounting for wind and ice
 * @param {AbstractCable} cable Cable
 * @param {LoadCase} loadCase Weather condition
 * @returns {number} Total linear weight of cable
 */
export function calcTotalLinearWeight(cable, loadCase, iceType = 'glaze') {
  const { windPressure, iceRadius, conductorConstant } = loadCase; // Wind pressure: [psf], iceRadius [in], conductorConstant [lb/ft]
  const cableLinearWeight = cable.linearWeight; // [lb/ft]
  const iceDensity = getIceDensity(iceType); // [lb/ft^3]
  const iceLinearWeight = ((iceDensity * Math.PI * iceRadius) / 144) * (cable.diameter + iceRadius); // [lb/ft]
  const windLinearWeight = windPressure * ((cable.diameter + 2 * iceRadius) / 12); // [lb/ft]
  const totalLinearWeight = Math.sqrt((cableLinearWeight + iceLinearWeight) ** 2 + windLinearWeight ** 2) + conductorConstant; // [lb/ft]

  return totalLinearWeight;
}

/**
 * Get ice density based on ice type
 * @param {string} iceType Type of ice
 * @returns {number} Density of ice
 */
function getIceDensity(iceType) {
  const iceName = iceType.toLowerCase();
  let iceDensity;
  switch (iceName) {
    case 'glaze':
      iceDensity = GLAZE_ICE_DENSITY;
      break;
    case 'rime':
      iceDensity = RIME_ICE_DENSITY;
      break;
    default:
      throw new Error('Invalid ice type');
  }

  return iceDensity;
}

/**
 * Modified Newton-Raphson method for function of single variable
 * @param {function} f Function to calculate zero
 * @param {number} a Initial guess
 * @param {number} stepSize Step size of derivative (central difference stencil)
 * @param {number} errorThreshold Acceptable error threshold
 * @returns {number} Calculated root
 */
function _modifiedNewton(f, a, stepSize = 1e-5, errorThreshold = 1e-8, numStepLim = 4000) {
  let converged = false;
  let numSteps = 0;
  let xN = a;
  let error = Math.abs(f(xN));
  while (error > errorThreshold) {
    numSteps += 1;
    const proposedXn = xN - f(xN) / _centralDiff(f, xN, stepSize);
    if (proposedXn < 0) {
      xN /= 2;
    } else {
      xN = proposedXn;
    }
    error = Math.abs(f(xN));
    if (numSteps > numStepLim) {
      break;
    }
  }

  if (error < errorThreshold) converged = true;

  let results = {
    root: xN,
    numSteps: numSteps,
    finalError: error,
    converged: converged
  };
  return results;
}

/**
 * Calculates the central numerical derivative of single variable function f at point a ( f'(a) )
 * @param {function} f Function to take the derivative of
 * @param {number} a Point to take derivative
 * @param {number} h Step size
 * @returns Numerical derivative of f at a ( f'(a) )
 */
function _centralDiff(f, a, h = 1e-5) {
  let derivative = (f(a + h) - f(a - h)) / (2 * h);

  return derivative;
}

/** Base class of all cables */
class AbstractCable {
  /**
   * Create AbstractCable
   * @param {number} spanLength Span length [ft]
   * @param {number} diameter Diameter [in]
   * @param {number} linearWeight Linear weight of cable [lb/ft]
   * @param {number} crossSectionalArea Cross-sectional area of cable [in^2]
   * @param {number} youngsMod Equivalent Young's modulus of cable [psi]
   * @param {number} thermalExpansionCoeff Equivalent coefficient of linear thermal expansion of cable [°F^-1]
   * @param {LoadCase} loadCase Weather condition where this specific tension exists
   */
  constructor(spanLength, diameter, linearWeight, crossSectionalArea, youngsMod, thermalExpansionCoeff, loadCase) {
    this.spanLength = spanLength;
    this.diameter = diameter;
    this.linearWeight = linearWeight;
    this.crossSectionalArea = crossSectionalArea;
    this.youngsMod = youngsMod;
    this.thermalExpansionCoeff = thermalExpansionCoeff;
    this.loadCase = loadCase;
  }
}

/** Class to represent cable that has known catenary shape */
export class GeometricCable extends AbstractCable {
  /**
   * Create GeometricCable
   * Shape of y(x) = catenaryYc + catenaryParam*(cosh((x - catenaryXc) / catenaryParam) - 1)
   * @param {number} spanLength Span length [ft]
   * @param {number} diameter Diameter [in]
   * @param {number} linearWeight Linear weight of cable [lb/ft]
   * @param {number} crossSectionalArea Cross-sectional area of cable [in^2]
   * @param {number} youngsMod Equivalent Young's modulus of cable [psi]
   * @param {number} thermalExpansionCoeff Equivalent coefficient of linear thermal expansion of cable [°F^-1]
   * @param {LoadCase} loadCase Weather condition where this specific catenary exists
   * @param {number} catenaryParam Catenary parameter [ft]
   * @param {number} catenaryXc Catenary x-translation [ft]
   * @param {number} catenaryYc Catenary y-translation [ft]
   */
  constructor(
    spanLength,
    diameter,
    linearWeight,
    crossSectionalArea,
    youngsMod,
    thermalExpansionCoeff,
    loadCase,
    catenaryParam,
    catenaryXc,
    catenaryYc
  ) {
    super(spanLength, diameter, linearWeight, crossSectionalArea, youngsMod, thermalExpansionCoeff, loadCase);
    this.catenaryParam = catenaryParam;
    this.catenaryXc = catenaryXc;
    this.catenaryYc = catenaryYc;
  }
}

/** Class representing cable with lookup tension */
export class LookupCable extends AbstractCable {
  /**
   * Create LookupCable
   * @param {number} spanLength Span length [ft]
   * @param {number} diameter Diameter [in]
   * @param {number} linearWeight Linear weight of cable [lb/ft]
   * @param {number} crossSectionalArea Cross-sectional area of cable [in^2]
   * @param {number} youngsMod Equivalent Young's modulus of cable [psi]
   * @param {number} thermalExpansionCoeff Equivalent coefficient of linear thermal expansion of cable [°F^-1]
   * @param {LoadCase} loadCase Weather condition where this specific tension exists
   * @param {number} horizontalTension Horizontal tension of cable (usually from lookup)
   */
  constructor(spanLength, diameter, linearWeight, crossSectionalArea, youngsMod, thermalExpansionCoeff, loadCase, horizontalTension) {
    super(spanLength, diameter, linearWeight, crossSectionalArea, youngsMod, thermalExpansionCoeff, loadCase);
    this.horizontalTension = horizontalTension;
  }
}

/** Class representing bundle with lookup tension */
export class LookupBundle extends LookupCable {
  /**
   * Create LookupBundle
   * @param {number} spanLength Span length [ft]
   * @param {number} circumscribedDiameter Circumscribed bundle diameter [in]
   * @param {number} messengerLinearWeight Linear weight of messenger cable [lb/ft]
   * @param {number} bundleComponentLinearWeight Linear weight of bundle components [lb/ft]
   * @param {number} messengerCrossSectionalArea Cross-sectional area of messenger cable [in^2]
   * @param {number} messengerYoungsMod Equivalent Young's modulus of messenger cable [psi]
   * @param {number} messengerThermalExpansionCoeff Equivalent coefficient of linear thermal expansion of messenger cable [°F^-1]
   * @param {LoadCase} loadCase Weather condition where this specific tension exists
   * @param {number} horizontalTension Horizontal tension of cable (usually from lookup)
   */
  constructor(
    spanLength,
    circumscribedDiameter,
    messengerLinearWeight,
    bundleComponentLinearWeight,
    messengerCrossSectionalArea,
    messengerYoungsMod,
    messengerThermalExpansionCoeff,
    loadCase,
    horizontalTension
  ) {
    const bundleWeight = messengerLinearWeight + bundleComponentLinearWeight;
    super(
      spanLength,
      circumscribedDiameter,
      bundleWeight,
      messengerCrossSectionalArea,
      messengerYoungsMod,
      messengerThermalExpansionCoeff,
      loadCase,
      horizontalTension
    );
    this.messengerLinearWeight = messengerLinearWeight;
    this.bundleComponentLinearWeight = bundleComponentLinearWeight;
  }
}

/** Class representing load case */
export class LoadCase {
  /**
   * Create LoadCase
   * @param {number} windPressure Wind Pressure [psf]
   * @param {number} iceRadius Ice radius [in]
   * @param {number} temperature Temperature [°F]
   * @param {number} conductorConstant Conductor constant [lb/ft]
   * @param {*} description Description of load case
   */
  constructor(windPressure, iceRadius, temperature, conductorConstant, description = null) {
    this.windPressure = windPressure;
    this.iceRadius = iceRadius;
    this.temperature = temperature;
    this.conductorConstant = conductorConstant;
    this.description = description;
  }
}

// const loadCaseI = new LoadCase(
//     4,
//     0.5,
//     60,
//     0.0,
//     'Initial Load Case'
// )

// const loadCaseF = new LoadCase(
//     6,
//     0.25,
//     40,
//     0,
//     'Final Load Case'
// )

// const bundle = new LookupBundle(
//     100,
//     0.5,
//     0.25,
//     0.4,
//     0.05,
//     300e6,
//     0.0000128,
//     loadCaseI,
//     1000
// );
// const final = transformLookupCable(bundle, loadCaseF);
// console.log(final)

// const cable = new LookupCable(
//     100,
//     0.5,
//     0.5,
//     0.75,
//     300e6,
//     0.0000128,
//     loadCaseI,
//     1000
// );

// const finalParams = transformLookupCable(cable, loadCaseF);
// console.log(finalParams);

// // const loadCaseI = new LoadCase(
// //     4,
// //     0.5,
// //     0.0,
// //     0.3,
// //     'Initial Load Case'
// // )

// // const cable = new LookupCable(
// //     null,
// //     0.25, // diameter
// //     0.5, // linear weight
// //     null,
// //     null,
// //     null,
// //     null,
// //     null
// // );

// // const totalLinearWeight = calcTotalLinearWeight(cable, loadCaseI);
// // console.log('totalLinearWeight', totalLinearWeight, ' lb/ft');
